import _ from 'lodash';
import { Answer, AnswerEditSession } from '/@services/generated/graphql-types';
import {
    ApolloCache,
    DocumentNode,
    Reference,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { readFromLBChartVizCache } from '../../utils/lb-chartViz-caching-service';

export const getAnswerCacheKey = (
    cache: ApolloCache<object>,
    answerSessionId: string,
): string => {
    const cacheObj = {
        __typename: 'AnswerEditSession',
        id: { sessionId: answerSessionId },
    };
    return cache.identify(cacheObj);
};
export const fetchVizRefsForAnswerSession = (
    cache: ApolloCache<object>,
    answerSessionId: string,
    // cache.extract() shallow-copies the whole normalized cache; callers
    // reading many sessions in one pass should extract once and pass it in.
    snapshot?: Record<string, unknown>,
) => {
    const cacheData = snapshot ?? (cache.extract() as Record<string, unknown>);
    const answerSession = cacheData[
        getAnswerCacheKey(cache, answerSessionId)
    ] as { answer?: { __ref?: string } } | undefined;
    const cachedAnswerRef: string | undefined = answerSession?.answer?.__ref;
    const cachedAnswer = cachedAnswerRef
        ? cacheData[cachedAnswerRef]
        : undefined;

    return (
        (cachedAnswer as { visualizations?: Reference[] } | undefined)
            ?.visualizations || []
    );
};

export const getCachedAnswer = (
    cache: ApolloCache<object>,
    answerSessionId: string,
    query: DocumentNode,
    returnPartialData = false,
    options?: {
        /**
         * Skip the defensive deep clone of the read result (which copies
         * every data row) and shallow-copy only the levels this function
         * reassigns. Safe for callers that never mutate the result.
         */
        readOnly?: boolean;
        /**
         * Lazily provides a pre-extracted cache snapshot (see
         * fetchVizRefsForAnswerSession). Only invoked on the LB chart-viz
         * cache path, so callers can defer the extract until it is needed.
         */
        getSnapshot?: () => Record<string, unknown>;
    },
): Partial<AnswerEditSession> => {
    const cachedAnswerOriginal: Partial<AnswerEditSession> = cache.readQuery({
        id: getAnswerCacheKey(cache, answerSessionId),
        query,
        returnPartialData,
    });
    if (!cachedAnswerOriginal) return cachedAnswerOriginal;
    const cachedAnswer = options?.readOnly
        ? {
              ...cachedAnswerOriginal,
              ...(cachedAnswerOriginal.answer
                  ? { answer: { ...cachedAnswerOriginal.answer } }
                  : {}),
          }
        : _.cloneDeep(cachedAnswerOriginal);
    const visualizations = cachedAnswer?.answer?.visualizations;

    // In case of liveboard load, we evict the chart viz cache from apollo cache
    // and store in local cache to avoid memory bloat by apollo watchers
    // Any component that needs chart viz will read from local cache
    // Note:- If there is any change in logic of fetching chat viz data from cache
    // Update useGetAnswerColumnsAndDataQuery in answer-service as well | SCAL-191717
    if (
        visualizations?.length === 0 &&
        fetchVizRefsForAnswerSession(
            cache,
            answerSessionId,
            options?.getSnapshot?.(),
        ).length !== 0
    ) {
        const chartVizCache = readFromLBChartVizCache(answerSessionId)
            ?.chartViz;
        if (chartVizCache) {
            // apollo stores same reference for all empty arrays
            // here filterGroups, parameters, sortInfo and visualizations
            // so if we try to push directly to existing ref, all objects will get same value
            // so we need to create new object and assign to visualizations
            cachedAnswer.answer.visualizations = [chartVizCache];
        }
    }
    return cachedAnswer;
};

const getRootIdsFromTypename = (
    cache: ApolloCache<object>,
    typename: string,
) => {
    const rootCacheData: any = cache.extract();
    const extraRootIds = rootCacheData?.__META?.extraRootIds ?? [];
    return extraRootIds.filter((id: string) => id.startsWith(typename));
};

export const getAnswerCacheKeys = (cache: ApolloCache<object>): string[] => {
    const typename = 'Answer:';
    return getRootIdsFromTypename(cache, typename);
};

export const getAcSessionCacheKeys = (cache: ApolloCache<object>): string[] => {
    const typename = 'acSession:';
    return getRootIdsFromTypename(cache, typename);
};

export const getAnswerSessionCacheKeys = (
    cache: ApolloCache<object>,
): string[] => {
    const typename = 'AnswerEditSession:';
    return getRootIdsFromTypename(cache, typename);
};

export const getVizCacheKeys = (cache: ApolloCache<object>): string[] => {
    const vizCacheKeys: string[] = [];
    const answerCacheKeys = getAnswerCacheKeys(cache);
    const rootCacheData: any = cache.extract();
    answerCacheKeys.forEach((key: string) => {
        const answer: Answer = rootCacheData[key];
        const vizes: any[] = answer?.visualizations ?? [];
        vizes.forEach(viz => {
            /* eslint no-underscore-dangle: 0 */
            vizCacheKeys.push(viz.__ref);
        });
    });
    return vizCacheKeys;
};
